import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Server always returns 200 to prevent enumeration; this catch is only for transport errors.
      toast.error(err.response?.data?.detail || 'Could not send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="bg-white border border-slate-200 rounded-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <Mail size={20} className="text-slate-900" />
            <h1 className="text-2xl font-chivo font-bold text-slate-900">Reset password</h1>
          </div>
          <p className="text-sm text-slate-600 font-ibm mb-6">
            Enter your email and we&apos;ll send you a one-time link to choose a new password.
          </p>

          {sent ? (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4" data-testid="forgot-success">
              <p className="text-sm font-ibm text-slate-900">
                If an account exists for <strong>{email}</strong>, a reset link has been sent.
                Open it within 1 hour to choose a new password.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  Email
                </Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                />
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/auth"
              data-testid="forgot-back-to-login"
              className="text-sm font-ibm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
